import { BaseService } from "@/api/base-service";
import type {
  CreateStaffMemberJsonPayload,
  StaffMember,
  StaffMemberList,
  StaffMembersParams,
  UpdateStaffMemberJsonPayload,
} from "./types";
import { buildQueryParams } from "@/utils/build-query-params";

export class StaffService extends BaseService {
  static get instance() {
    return this.getInstance<StaffService>();
  }

  public async getStaffMembers(params: StaffMembersParams = {}) {
    const queryParams = buildQueryParams(params);
    const url = `/staff_members${queryParams ? `?${queryParams}` : ""}`;
    return this.client.getWithHeaders<{ staffMembers: StaffMemberList[] }>(url);
  }

  public async getStaffMember(id: number): Promise<StaffMember> {
    const response = await this.client.get<{ staffMember: StaffMember }>(
      `/staff_members/${id}`,
    );
    return response.staffMember;
  }

  public async createStaffMember(
    payload: CreateStaffMemberJsonPayload,
  ): Promise<StaffMember> {
    const response = await this.client.post<{ staffMember: StaffMember }>(
      `/staff_members`,
      payload,
    );
    return response.staffMember;
  }

  public async createStaffMemberFormData(
    formData: FormData,
  ): Promise<StaffMember> {
    const response = await this.client.post<{ staffMember: StaffMember }>(
      `/staff_members`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.staffMember;
  }

  public async updateStaffMember(
    id: number,
    payload: UpdateStaffMemberJsonPayload,
  ): Promise<StaffMember> {
    const response = await this.client.patch<{ staffMember: StaffMember }>(
      `/staff_members/${id}`,
      payload,
    );
    return response.staffMember;
  }

  public async updateStaffMemberFormData(
    id: number,
    formData: FormData,
  ): Promise<StaffMember> {
    const response = await this.client.patch<{ staffMember: StaffMember }>(
      `/staff_members/${id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.staffMember;
  }

  public async deleteStaffMember(id: number): Promise<void> {
    await this.client.delete(`/staff_members/${id}`);
  }

  public async resendConfirmation(id: number): Promise<void> {
    await this.client.post(
      `/staff_members/${id}/resend_confirmation`,
      undefined,
    );
  }
}

export default StaffService;
